import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MaskedLines, Reveal, FadeIn } from "../components/site/Reveal";
import { submitInquiry } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

const Field = ({ label, name, type = "text", value, onChange, required, textarea }) => (
  <div className="relative">
    <label htmlFor={name} className="eyebrow text-ink/50 block mb-3">
      {label}
      {required && <span className="text-brass ml-1">*</span>}
    </label>
    {textarea ? (
      <textarea
        id={name}
        name={name}
        data-testid={`contact-${name}`}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        className="w-full bg-transparent border-0 border-b border-stone py-3 text-lg text-ink font-light placeholder-ink/30 focus:outline-none focus:border-ink transition-colors duration-400 resize-none"
      />
    ) : (
      <input
        id={name}
        name={name}
        type={type}
        data-testid={`contact-${name}`}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-0 border-b border-stone py-3 text-lg text-ink font-light placeholder-ink/30 focus:outline-none focus:border-ink transition-colors duration-400"
      />
    )}
  </div>
);

export default function Contact() {
  const [searchParams] = useSearchParams();
  const journey = searchParams.get("journey");
  const [form, setForm] = useState({
    name: "",
    organization: "",
    role: "",
    email: "",
    message: journey
      ? `I'd like to learn more about the ${journey} journey with Yadid Journeys.`
      : "",
    website: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await submitInquiry(form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-40 md:pt-52 pb-16 md:pb-20">
        <div className="yadid-container">
          <FadeIn delay={0.1}>
            <p className="eyebrow text-brass mb-8">Contact</p>
          </FadeIn>
          <MaskedLines
            tag="h1"
            lines={["Start a conversation."]}
            delay={0.2}
            className="font-serif text-ink text-[2.4rem] sm:text-5xl lg:text-[3.7rem] xl:text-[4.1rem] leading-[1.04] tracking-[-0.025em]"
          />
          <Reveal delay={0.4} className="mt-10 max-w-2xl">
            <p className="text-lg md:text-xl text-ink/70 font-light leading-relaxed">
              Some situations are clear before the first conversation. Others become clear because of
              it. If you are navigating a consequential opportunity, relationship, decision, or
              institutional moment, we would be glad to hear what is on your mind.
            </p>
          </Reveal>
          {journey && (
            <Reveal delay={0.5} className="mt-8">
              <div
                data-testid="contact-journey-note"
                className="inline-flex items-center gap-3 border border-brass/40 bg-brass/5 px-5 py-3"
              >
                <span className="eyebrow text-brass">Enquiry</span>
                <span className="text-ink/75 font-light">
                  About the <span className="text-ink font-normal">{journey}</span> journey
                </span>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="bg-ivory border-t border-stone">
        <div className="yadid-container py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              {status === "success" ? (
                <motion.div
                  data-testid="contact-success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="border border-stone p-10 md:p-16 bg-stone-soft/30"
                >
                  <CheckCircle2 className="text-brass mb-6" size={40} strokeWidth={1.4} />
                  <h2 className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                    Thank you. Your message has been received.
                  </h2>
                  <p className="mt-5 text-ink/65 font-light leading-relaxed text-lg">
                    Someone from Yadid will be in touch.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                    <Field label="Name" name="name" value={form.name} onChange={onChange} required />
                    <Field
                      label="Organization"
                      name="organization"
                      value={form.organization}
                      onChange={onChange}
                    />
                    <Field label="Role" name="role" value={form.role} onChange={onChange} />
                    <Field
                      label="Email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      required
                    />
                  </div>
                  <Field
                    label="What prompted the conversation?"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    textarea
                  />

                  {/* Honeypot — hidden from users */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="hidden"
                  />

                  {status === "error" && (
                    <p data-testid="contact-error" className="text-sm text-red-700 font-light">
                      Something went wrong. Please try again, or write to us directly.
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-3 bg-ink text-ivory px-10 py-4 text-[0.78rem] tracking-[0.16em] uppercase font-semibold transition-all duration-500 hover:bg-[#16294c] disabled:opacity-50"
                  >
                    {status === "loading" ? "Sending…" : "Start the Conversation"}
                  </button>
                </form>
              )}
            </div>

            <div className="lg:col-span-4 lg:col-start-9">
              <div className="border-l border-stone pl-8">
                <p className="eyebrow text-brass mb-6">Discretion</p>
                <p className="text-ink/70 font-light leading-relaxed">
                  Every inquiry is treated with discretion. Submitting this form does not create an
                  advisory relationship.
                </p>
                <div className="rule my-10" />
                <p className="eyebrow text-brass mb-6">The First Step</p>
                <p className="font-serif text-2xl md:text-3xl text-ink italic leading-snug">
                  When the moment matters, the first step may simply be a trusted conversation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
